/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { GlossaryCollectionTable } from './GlossaryCollectionTable';
import { app } from '@ink-ai/portal/reducers/app';
import { useDispatch } from 'react-redux';
import { GlossaryCollectionDto } from '@ink-ai/insight-service-sdk';
import { glossaryActions } from '@ink-ai/portal/reducers/glossary';
import { isOffice } from '@ink-ai/portal/common/utils';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

interface GlossaryDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedGlossaries: string[], selectedFolders: string[]) => void;
  initialSelectedGlossaries?: string[];
  initialSelectedFolders?: string[];
  // Enable cascading selection behavior on folder selection
  cascadeSelection?: boolean;
  // 控制是否在切换文件夹/返回根目录时重置勾选（列表页可能需要，翻译页不需要）
  resetSelectionOnFolderNav?: boolean;
}

export const GlossaryDialog: React.FC<GlossaryDialogProps> = ({
  open,
  onClose,
  onApply,
  initialSelectedGlossaries,
  initialSelectedFolders,
  cascadeSelection = false,
  resetSelectionOnFolderNav = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [selectedGlossaries, setSelectedGlossaries] = useState<string[]>([]);
  const [selectedGlossaryFolders, setSelectedGlossaryFolders] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalItemCount, setTotalItemCount] = useState(0);
  const [mixedItems, setMixedItems] = useState<any[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<{ id: string | null; name: string }[]>([{ id: null, name: t('glossary.navigation.glossary') || 'Glossary' }]);

  const handleApply = () => {
    onApply(selectedGlossaries, selectedGlossaryFolders);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNavigate = (glossary: GlossaryCollectionDto) => {
    dispatch(glossaryActions.setSelectedGlossary(glossary));
    window.open(`/glossary/${glossary.uuid}`, '_blank');
  };

  const loadExplorer = async () => {
    try {
      const params: Record<string, any> = {
        page: page + 1,
        pageSize: rowsPerPage,
      };
      if (searchTerm && searchTerm.trim().length > 0)
        params.name = searchTerm.trim();
      if (currentFolderId) params.parentId = currentFolderId;
      params.status = 'Ready';
      const data: any = await makeRequest({
        method: 'GET',
        url: '/api/v1/glossary/glossaryExplorer',
        params,
      });
      // API returns explorer data under `collections`; folders have itemType: 'folder', files have itemType: 'file'
      const rawItems = data.collections || data.data?.collections || data.items || data.data?.items || [];
      const total = data.totalCount ?? data.data?.totalCount ?? rawItems.length;
      setTotalItemCount(total);
      const toMillis = (v: any) => (typeof v === 'number' ? v : (v ? new Date(v).getTime() : undefined));
      const itemsMapped = (rawItems as any[]).map((it: any) => {
        const isFolder = String(it.itemType) === 'folder';
        const isFile = String(it.itemType) === 'file' || (!it.itemType && it.status); // be tolerant in case itemType missing
        const childrenRaw: any[] = Array.isArray(it.collections) ? it.collections : [];
        const children = childrenRaw
          .filter((c) => String(c.itemType) !== 'folder')
          .map((c) => ({
            uuid: c.uuid,
            type: 'glossary',
            status: c.status,
            parentId: it.uuid,
          }));
        return {
          uuid: it.uuid,
          name: it.name,
          type: isFolder ? 'folder' : 'glossary',
          desc: isFile ? it.desc : undefined,
          status: isFile ? it.status : undefined,
          itemCount: isFile ? (it.itemsCount ?? it.itemCount) : undefined,
          updatedAt: toMillis(it.updatedAt),
          parentId: it.parentId ?? null,
          // For folders, attach direct child files for cascade selection when at root
          children: isFolder ? children : undefined,
        };
      });
      setMixedItems(itemsMapped);
    } catch (e) {
      console.error('Failed to load glossary explorer in dialog:', e);
    }
  };

  // 仅负责加载列表（随页码/搜索/文件夹变化）
  useEffect(() => {
    if (!open) return;
    loadExplorer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, rowsPerPage, searchTerm, currentFolderId]);

  // 同步初始选中项到内部状态：只在打开弹窗或初始值变化时进行
  useEffect(() => {
    if (!open) return;
    setSelectedGlossaries(initialSelectedGlossaries ?? []);
    setSelectedGlossaryFolders(initialSelectedFolders ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSelectedGlossaries, initialSelectedFolders]);

  // 可选：在切换文件夹时重置（用于列表页需求）
  useEffect(() => {
    if (!open || !resetSelectionOnFolderNav) return;
    setSelectedGlossaries(initialSelectedGlossaries ?? []);
    setSelectedGlossaryFolders(initialSelectedFolders ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetSelectionOnFolderNav, currentFolderId]);

  const currentFolderName = useMemo(
    () => folderStack[folderStack.length - 1]?.name,
    [folderStack],
  );

  const handleEnterFolder = (folder: { uuid: string; name?: string }) => {
    setCurrentFolderId(folder.uuid);
    setFolderStack((prev) => [
      ...prev,
      { id: folder.uuid, name: folder.name || (t('glossary.navigation.glossary') as string) },
    ]);
    setPage(0);
  };

  const handleBack = () => {
    if (folderStack.length <= 1) return;
    const next = [...folderStack];
    next.pop();
    setFolderStack(next);
    setCurrentFolderId(next[next.length - 1]?.id || null);
    setPage(0);
  };

  const isInOffice = isOffice();

  return (
    <Dialog
      fullScreen={isInOffice}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {folderStack.length > 1 ? (
          <IconButton aria-label="back" onClick={handleBack} size="small">
            <ArrowBackOutlinedIcon />
          </IconButton>
        ) : null}
        {currentFolderId ? currentFolderName : t('glossary.chooseGlossaries')}
      </DialogTitle>
      <DialogContent>
        <TextField
          label={t('common.search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
          InputLabelProps={{
            shrink: true,
          }}
          margin="dense"
        />
        <GlossaryCollectionTable
          selectedGlossaries={selectedGlossaries}
          selectedFolders={selectedGlossaryFolders}
          onSelectedGlossariesChange={setSelectedGlossaries}
          onSelectedFoldersChange={setSelectedGlossaryFolders}
          currentFolderId={currentFolderId}
          searchTerm={searchTerm}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onNavigate={handleNavigate}
          onEnterFolder={handleEnterFolder}
          onTotalItemCountChange={setTotalItemCount}
          allowNonReadySelection={false}
          enableSelection
          showActions={false}
          mixedItems={mixedItems}
          totalCount={totalItemCount}
          cascadeSelection={cascadeSelection}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
        >
          {t('common.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
